import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import { StreakType } from 'utils/types';
import { CloseOutlineIcon, InfoIcon } from 'cokitchen-icons';
import { toast } from 'react-toastify';
import MetadataTag from 'components/base/metadata-tag';
import { apiService } from 'services/base.service';

type FormData = {
  weeks: number | null;
  multiplier_effect_percentage: number | null;
};

const initialFormData = {
  weeks: null,
  multiplier_effect_percentage: null
};

type CreateProps = {
  action: 'create';
  open: boolean;
  onClose: () => void;
  afterMutation?: () => void;
};

type EditProps = {
  action: 'edit';
  open: boolean;
  streak: StreakType;
  onClose: () => void;
  afterMutation?: () => void;
};

type BulkEditProps = {
  action: 'bulk-edit';
  open: boolean;
  streaks: StreakType[];
  onClose: () => void;
  afterMutation?: () => void;
};

type Props = CreateProps | EditProps | BulkEditProps;

const StreakFormModal = (props: Props) => {
  const { open, action, onClose, afterMutation } = props;
  const [streaks, setStreaks] = useState<StreakType[]>([]);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [loading, setLoading] = useState(false);

  const getDefaultValue = (field: keyof StreakType) => {
    if (action === 'edit') return props.streak[field].toString();
  };

  const removeStreak = (index: number) => {
    if (action !== 'bulk-edit') return;
    if (streaks.length < 2) return onClose();
    setStreaks(streaks.filter((_, i) => i !== index));
  };

  const createStreak = async () => {
    try {
      await apiService.post('internal/loyalty-streak', {
        data: formData,
        isInternal: true
      });
      afterMutation?.();
      setLoading(false);
      onClose();
      toast(<ToastMessage type='success' message='Streak created succesfully' />);
    } catch (error) {
      console.log(error);
    }
  };

  const editStreak = async () => {
    if (action !== 'edit') return;
    try {
      await apiService.put(`internal/loyalty-streak/${props.streak.id}`, {
        data: formData,
        isInternal: true
      });
      afterMutation?.();
      setLoading(false);
      onClose();
      toast(<ToastMessage type='success' message='Streak edited succesfully' />);
    } catch (error) {
      console.log(error);
    }
  };

  const bulkEditStreak = async () => {
    if (action !== 'bulk-edit') return;
    try {
      await apiService.post('internal/loyalty-streak/bulk-update', {
        data: { ...formData, streak_ids: streaks.map((s) => s.id) },
        isInternal: true
      });
      afterMutation?.();
      setLoading(false);
      onClose();
      toast(<ToastMessage type='success' message='Streaks edited succesfully' />);
    } catch (error) {
      console.log(error);
    }
  };

  const submitForm = () => {
    setLoading(true);
    if (action === 'edit') {
      editStreak();
    } else if (action === 'bulk-edit') {
      bulkEditStreak();
    } else {
      createStreak();
    }
  };

  useEffect(() => {
    if (action === 'edit')
      setFormData({
        weeks: props.streak.weeks,
        multiplier_effect_percentage: props.streak.multiplier_effect_percentage
      });
    if (action === 'bulk-edit') setStreaks(props.streaks);
  }, []);

  return (
    <Modal isOpen={open} style={modalStyles} onRequestClose={onClose} ariaHideApp={false}>
      <div className='flex flex-col'>
        <div className='relative flex items-center justify-center p-4 border-b-[0.8px] border-[#EFF0F0]'>
          <p className='text-[#1A1A1A] font-medium'>
            {action === 'create'
              ? 'Create new streak'
              : action === 'edit'
              ? `Edit ${props.streak.name}`
              : 'Bulk Edit'}
          </p>

          <button
            className='w-[25px] h-[25px] flex items-center justify-center bg-[#EFF0F0] [&_svg]:w-[19px] [&_svg]:h-[19px] [&_path]:fill-[#313435] rounded-full absolute right-6 top-4'
            onClick={onClose}>
            <CloseOutlineIcon />
          </button>
        </div>
        {action === 'create' ? (
          <div className='w-full flex items-center justify-center gap-2 p-[10px] border-y-[0.8px] border-[#EFF0F0] bg-[#F7F7F7]'>
            <InfoIcon className='[&_path]:fill-[#BD8F05]' />
            <p className='text-xs text-[#85806F]'>Streak name is auto-generated</p>
          </div>
        ) : null}
        <div className='flex flex-col gap-6 p-6'>
          {action === 'bulk-edit' ? (
            <div className='flex flex-col gap-1'>
              <p className='font-medium text-[#1A1A1A]'>Streak name</p>
              <div className='flex items-start gap-2 flex-wrap'>
                {streaks.map((streak, streakIndex) => (
                  <MetadataTag
                    key={streakIndex}
                    name={streak.name}
                    hasDeleteBtn
                    onDelete={() => removeStreak(streakIndex)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <Input
            small
            type='number'
            label='Weeks'
            placeholder='Enter weeks e.g 1'
            defaultValue={getDefaultValue('weeks')}
            onChange={(e) => setFormData((f) => ({ ...f, weeks: Number(e.target.value) }))}
          />
          <Input
            small
            type='number'
            label='Multiplier effect'
            placeholder='12%'
            defaultValue={getDefaultValue('multiplier_effect_percentage')}
            onChange={(e) =>
              setFormData((f) => ({ ...f, multiplier_effect_percentage: Number(e.target.value) }))
            }
          />

          <div className='w-[90%] mx-auto pt-2'>
            <Button
              variant='block'
              color='primary'
              disabled={!Object.values(formData).every(Boolean)}
              onClick={submitForm}
              loading={loading}>
              {action === 'edit' ? 'Update' : 'Confirm'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '10px',
    backgroundColor: '#fff',
    border: 'none',
    padding: '0px',
    overflow: 'unset',
    maxWidth: '520px',
    width: '60%'
  },
  overlay: {
    background: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(3px)',
    zIndex: 10000
  }
};

export default StreakFormModal;
